import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { displayTitle } from './format';
import { C, MONO } from './tokens';
import { RestockV2ThumbTarget } from './types';

// Staff identify a row by the garment, not by the SKU string — so every card, tile and strip leads
// with this. 3:4 like the product images themselves, so the crop never lies about the shape.
interface ProductThumbProps {
  target: RestockV2ThumbTarget;
  width?: number | string;
  height?: number | string;
  radius?: number;
  // Rack tiles are fluid: fill the grid cell and keep the 3:4 shape instead of a fixed pixel box.
  aspect?: boolean;
  // Omitted on a non-interactive surface; otherwise a tap opens the gallery dialog.
  onOpen?: (target: RestockV2ThumbTarget) => void;
}

const ProductThumb: React.FC<ProductThumbProps> = ({
  target, width = 48, height = 64, radius = 4, aspect = false, onOpen,
}) => {
  // A dead CDN URL used to render the browser's broken-image glyph with wrapped alt text inside the
  // beige frame. Falling back to the SKU placeholder keeps the row readable and the grid aligned.
  const [broken, setBroken] = useState(false);
  const title = displayTitle(target.title) || target.sku;
  const src = broken ? '' : (target.image_url || '');
  const hasVariant = target.variant_id !== null && target.variant_id !== undefined;
  // Nothing to open when there is neither a picture nor a variant whose gallery we could fetch.
  const clickable = !!onOpen && (!!src || hasVariant);
  const frameSx = {
    width: aspect ? '100%' : width,
    ...(aspect ? { aspectRatio: '3 / 4' } : { height }),
    flexShrink: 0, borderRadius: `${radius}px`, overflow: 'hidden',
    bgcolor: C.paper2, border: '1px solid', borderColor: C.line,
  } as const;

  const frame = src ? (
    <Box sx={frameSx}>
      <Box
        component="img" src={src} alt={title} loading="lazy" onError={() => setBroken(true)}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </Box>
  ) : (
    <Box
      role="img" aria-label={`${target.sku} 無圖片`}
      sx={{ ...frameSx, display: 'grid', placeItems: 'center', px: 0.25 }}
    >
      <Typography
        variant="caption" sx={{ fontFamily: MONO, fontSize: 10, lineHeight: 1.1, color: C.ink3, textAlign: 'center', wordBreak: 'break-all' }}
      >
        {target.sku}
      </Typography>
    </Box>
  );

  if (!clickable) return frame;
  return (
    <Box
      component="button" type="button" aria-label={`查看 ${target.sku} ${title} 的商品圖片`}
      onClick={() => onOpen?.(target)}
      sx={{
        p: 0, m: 0, border: 0, bgcolor: 'transparent', lineHeight: 0, flexShrink: 0,
        cursor: 'pointer', borderRadius: `${radius}px`, display: 'block',
        ...(aspect ? { width: '100%' } : {}),
      }}
    >
      {frame}
    </Box>
  );
};

export default ProductThumb;
